import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Spinner, Alert } from 'react-bootstrap';
import OwnerTaskCard from './OwnerTaskCard';
import taskApi from '../services/taskApi';

const DATE_FILTER_LABELS = {
  week: 'هذا الأسبوع',
  month: 'هذا الشهر',
  all: 'كل المهام'
};

/**
 * Read-only Task Dashboard for Property Owners
 * Shows cleaning tasks status without any interactive controls
 * Part of fully managed service - owners have visibility only
 */
const OwnerTaskDashboard = ({ propertyId }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateFilter, setDateFilter] = useState('week');

  // Load tasks from API (read-only)
  const loadTasks = async () => {
    setLoading(true);
    setError(null);

    try {
      const days = dateFilter === 'week' ? 7 :
                   dateFilter === 'month' ? 30 : 365;

      const response = await taskApi.getPropertyTasks(propertyId, days);
      setTasks(response.data.tasks || []);
    } catch (err) {
      console.error('Load tasks error:', err);
      setError('فشل تحميل المهام. يرجى المحاولة مرة أخرى.');
    }

    setLoading(false);
  };

  // Load on mount and when filter changes
  useEffect(() => {
    loadTasks();
    // eslint-disable-next-line
  }, [propertyId, dateFilter]);

  // Group tasks by date (upcoming first, then completed)
  const groupTasksByDate = () => {
    const upcoming = [];
    const completed = [];

    tasks.forEach(task => {
      if (task.status === 'completed') {
        completed.push(task);
      } else {
        upcoming.push(task);
      }
    });

    // Sort upcoming by date (earliest first)
    upcoming.sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate));

    // Sort completed by completion date (most recent first)
    completed.sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt));

    return { upcoming, completed };
  };

  const { upcoming, completed } = groupTasksByDate();

  if (loading) {
    return (
      <div className="text-center py-5" dir="rtl">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">جاري تحميل المهام...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="m-3" dir="rtl">
        {error}
      </Alert>
    );
  }

  return (
    <Container fluid className="p-0" dir="rtl">
      {/* Header with info message */}
      <Row className="mb-3">
        <Col>
          <Alert variant="info" className="py-2 text-end" dir="rtl">
            <small>
              فريقنا يدير جميع مهام التنظيف لضمان جاهزية عقارك دائماً
            </small>
          </Alert>
        </Col>
      </Row>

      {/* Filter */}
      <Row className="mb-3">
        <Col xs={12} sm={6} md={4}>
          <Form.Select
            size="sm"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            dir="rtl"
          >
            <option value="week">{DATE_FILTER_LABELS.week}</option>
            <option value="month">{DATE_FILTER_LABELS.month}</option>
            <option value="all">{DATE_FILTER_LABELS.all}</option>
          </Form.Select>
        </Col>
      </Row>

      {/* Empty State */}
      {tasks.length === 0 ? (
        <Alert variant="info" dir="rtl" className="text-end">
          <Alert.Heading>لا توجد مهام مجدولة</Alert.Heading>
          <p className="mb-0">
            لا توجد مهام تنظيف مجدولة لهذا العقار في الفترة المحددة.
          </p>
        </Alert>
      ) : (
        <>
          {/* Upcoming Tasks */}
          {upcoming.length > 0 && (
            <section className="mb-4" dir="rtl">
              <h6 className="mb-2 text-muted" style={{ textAlign: 'right' }}>
                📅 المهام القادمة ({upcoming.length})
              </h6>
              {upcoming.map(task => (
                <OwnerTaskCard key={task._id} task={task} />
              ))}
            </section>
          )}

          {/* Completed Tasks */}
          {completed.length > 0 && (
            <section className="mb-4" dir="rtl">
              <h6 className="mb-2 text-muted" style={{ textAlign: 'right' }}>
                ✅ المهام المكتملة ({completed.length})
              </h6>
              {completed.map(task => (
                <OwnerTaskCard key={task._id} task={task} />
              ))}
            </section>
          )}
        </>
      )}

      {/* Future Feature Placeholder - Commented Out */}
      {/*
      <Row className="mt-5">
        <Col>
          <Alert variant="light" className="text-end" dir="rtl">
            <strong>هل لديك ملاحظات أو طلبات خاصة؟</strong>
            <p className="mb-2 small">قريباً ستتمكن من إضافة ملاحظات وطلبات خاصة مباشرة من هنا.</p>
            <Button size="sm" variant="outline-secondary" disabled>
              إضافة ملاحظة
            </Button>
          </Alert>
        </Col>
      </Row>
      */}
    </Container>
  );
};

export default OwnerTaskDashboard;
